/**
 * SSO Callback Handler
 * Handles OAuth and SAML authentication callbacks
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { useToast } from '../ui/use-toast';
import { useAuth } from '../../lib/auth/AuthContext';
import { ssoService, SSOProvider } from '../../lib/auth/sso-service';
import { auditService } from '../../lib/auth/audit-service';

export const SSOCallback: React.FC = () => {
  const { provider } = useParams<{ provider: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<any>(null);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      if (!provider || !['google', 'microsoft', 'okta', 'auth0', 'saml'].includes(provider)) {
        throw new Error('Invalid SSO provider');
      }

      const ssoProvider = provider as SSOProvider;

      // Extract callback data from URL params
      const callbackData = {
        code: searchParams.get('code') ?? undefined,
        state: searchParams.get('state') ?? undefined,
        error: searchParams.get('error') ?? undefined,
        error_description: searchParams.get('error_description') ?? undefined,
        saml_response: searchParams.get('saml_response') || searchParams.get('SAMLResponse') || undefined
      };

      let result;

      if (ssoProvider === 'saml') {
        // Handle SAML callback
        if (!callbackData.saml_response) {
          throw new Error('Missing SAML response');
        }
        result = await ssoService.handleSAMLCallback(callbackData);
      } else {
        // Handle OAuth callback
        if (callbackData.error) {
          throw new Error(callbackData.error_description || callbackData.error);
        }

        if (!callbackData.code) {
          throw new Error('Missing authorization code');
        }

        result = await ssoService.handleOAuthCallback(ssoProvider, callbackData);
      }

      // Set user information and update audit context
      setUserInfo(result.user);
      auditService.setUserContext(result.user);

      // Log successful SSO authentication
      await auditService.logAction({
        action: `sso:${ssoProvider}_success`,
        resource_type: 'authentication',
        details: {
          provider: ssoProvider,
          user_id: result.user.user_id,
          email: result.user.email,
          is_new_user: result.is_new_user,
          login_method: 'sso'
        }
      });

      setStatus('success');

      // Show success message
      toast({
        title: 'Welcome!',
        description: result.is_new_user
          ? 'Your account has been created and you are now signed in'
          : 'You have been successfully signed in',
        variant: 'default'
      });

      // Redirect to intended destination or overview
      const redirectTo = searchParams.get('state') || '/overview';
      setTimeout(() => {
        navigate(redirectTo);
      }, 2000);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'SSO authentication failed';

      setError(errorMessage);
      setStatus('error');

      // Log failed SSO attempt
      await auditService.logAction({
        action: `sso:${provider}_failed`,
        resource_type: 'authentication',
        details: {
          provider,
          error: errorMessage,
          callback_data: Object.fromEntries(searchParams)
        },
        success: false,
        error_message: errorMessage
      });

      toast({
        title: 'SSO Authentication Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-8 w-8 animate-spin text-blue-600" />;
      case 'success':
        return <CheckCircle className="h-8 w-8 text-green-600" />;
      case 'error':
        return <XCircle className="h-8 w-8 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'loading':
        return 'Processing your authentication...';
      case 'success':
        return 'Authentication successful! Redirecting...';
      case 'error':
        return 'Authentication failed';
      default:
        return '';
    }
  };

  const getProviderName = (provider?: string) => {
    const names = {
      google: 'Google',
      microsoft: 'Microsoft',
      okta: 'Okta',
      auth0: 'Auth0',
      saml: 'SAML'
    };
    return names[provider as keyof typeof names] || 'SSO Provider';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center space-y-6">
          <div className="flex justify-center">
            {getStatusIcon()}
          </div>

          <div className="space-y-2">
            <h1 className="text-xl font-semibold">
              {getProviderName(provider)} Authentication
            </h1>
            <p className="text-gray-600">
              {getStatusMessage()}
            </p>
          </div>

          {status === 'success' && userInfo && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
              <h3 className="font-medium text-green-900 mb-2">Welcome!</h3>
              <div className="text-sm text-green-700 space-y-1">
                <p><strong>Name:</strong> {userInfo.name}</p>
                <p><strong>Email:</strong> {userInfo.email}</p>
                <p><strong>Organization:</strong> {userInfo.organization?.name || 'Default'}</p>
                {userInfo.organization_memberships?.[0]?.role && (
                  <p><strong>Role:</strong> {userInfo.organization_memberships[0].role.replace('_', ' ')}</p>
                )}
              </div>
            </div>
          )}

          {status === 'error' && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p>{error}</p>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/auth/login')}
                      className="flex-1"
                    >
                      Back to Login
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.reload()}
                      className="flex-1"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {status === 'loading' && (
            <div className="text-sm text-gray-500">
              <p>Please wait while we verify your credentials...</p>
              <p className="mt-2">Do not close this window.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-2">
              <p className="text-sm text-gray-500">
                You will be redirected automatically in a few seconds.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/overview')}
                className="w-full"
              >
                Continue to Dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SSOCallback;